import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import connectUser from '../connectUser';
import Icon from '../ui/Icon';
import AppIcon from '../ui/AppIcon';
import Badge from '../ui/Badge';
import HiveButton from '../ui/HiveButton';
import { appById } from '../registry/appRegistry';
import { accessState, isBrowseVisible } from '../registry/selectors';
import { categoryLabel } from '../registry/categories';
import accessApi from '../accessApi';
import './screens.css';

function AppDetailScreen({ userInfo }) {
  const history = useHistory();
  const { appId } = useParams();
  const routes = userInfo?.routes ? [...userInfo.routes] : [];
  const app = appById(appId);
  const [toast, setToast] = useState(null);
  const [requested, setRequested] = useState(false);
  const [sending, setSending] = useState(false);

  // Reflect an existing pending request so the button reads "Requested".
  useEffect(() => {
    let alive = true;
    if (!app || accessState(app, routes) !== 'request') return undefined;
    accessApi
      .myRequests()
      .then((res) => {
        const mine = Array.isArray(res?.data) ? res.data : [];
        if (alive && mine.some((r) => r.appId === app.id && r.status === 'pending')) {
          setRequested(true);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  if (!app) {
    return (
      <div className="hive-container hive-screen">
        <button type="button" className="hive-backlink hive-tap" onClick={() => history.push('/store')}>
          <Icon name="ChevronLeft" size={18} /> Store
        </button>
        <div className="hive-empty"><p>That app doesn’t exist.</p></div>
      </div>
    );
  }

  // Admin-only apps the user hasn't been granted aren't available to them —
  // don't reveal a detail page even via a direct link.
  if (!isBrowseVisible(app, routes)) {
    return (
      <div className="hive-container hive-screen">
        <button type="button" className="hive-backlink hive-tap" onClick={() => history.push('/store')}>
          <Icon name="ChevronLeft" size={18} /> Store
        </button>
        <div className="hive-empty hive-empty-lg">
          <div className="hive-empty-ic"><Icon name="Lock" size={30} /></div>
          <p className="hive-empty-title">Not available</p>
          <p>This app isn’t available on your account.</p>
        </div>
      </div>
    );
  }

  const state = accessState(app, routes);

  const requestAccess = () => {
    if (sending || requested) return;
    setSending(true);
    accessApi
      .requestAccess(app.id)
      .then(() => {
        setRequested(true);
        setToast(`Access requested for ${app.name}. A master-admin will review it.`);
      })
      .catch(() => setToast(`Couldn’t send the request. Please try again.`))
      .finally(() => {
        setSending(false);
        window.setTimeout(() => setToast(null), 4000);
      });
  };

  return (
    <div className="hive-container hive-screen">
      <button type="button" className="hive-backlink hive-tap" onClick={() => history.goBack()}>
        <Icon name="ChevronLeft" size={18} /> Back
      </button>

      <section className="hive-detail-hero hive-rise">
        <AppIcon app={app} size={84} />
        <div className="hive-detail-head">
          <h1 className="hive-h2">{app.name}</h1>
          <div className="hive-detail-tags">
            <Badge tone="muted" icon={CATEGORY_ICON}>{categoryLabel(app.category)}</Badge>
            {app.status === 'live' ? <Badge tone="live" icon="Check">Available</Badge>
              : <Badge tone="gold" icon="Clock">Coming soon</Badge>}
            {app.scope === 'personal' && <Badge tone="muted" icon="User">Personal</Badge>}
          </div>
        </div>
      </section>

      <div className="hive-detail-action">
        {state === 'open' && (
          <HiveButton variant="gold" full onClick={() => history.push(app.path)}>
            Open {app.name}
          </HiveButton>
        )}
        {state === 'request' && (
          requested ? (
            <HiveButton variant="soft" full disabled>
              <Icon name="Clock" size={16} /> Requested — pending approval
            </HiveButton>
          ) : (
            <HiveButton variant="ghost" full disabled={sending} onClick={requestAccess}>
              <Icon name="Lock" size={16} /> {sending ? 'Sending…' : 'Request access'}
            </HiveButton>
          )
        )}
        {state === 'coming-soon' && (
          <HiveButton variant="soft" full disabled>
            Coming soon
          </HiveButton>
        )}
      </div>

      {app.description && (
        <section className="hive-detail-section">
          <h2 className="hive-section-title">About</h2>
          <p className="hive-detail-desc">{app.description}</p>
        </section>
      )}

      {toast && <div className="hive-toast" role="status">{toast}</div>}
    </div>
  );
}

const CATEGORY_ICON = 'Tag';

export default connectUser(AppDetailScreen);
